/**
 * 売上集計期間（締日）ユーティリティ v4.0.0
 *
 * Shop.salesPeriod.closingDay で締日を設定:
 *   - 数値 1-31: その日が締日（例: 15 → 毎月16日〜翌15日）
 *   - 'monthEnd': 月末締め（毎月1日〜末日）
 *
 * v4.0.0 で直したこと:
 *  1. 締日30/31 で `new Date(y, m, 32)` のような日付を作って翌月へ繰り上がり、
 *     期間が31〜32日になって前後の期と重なっていた（2月度と3月度に同じ日が入った）。
 *     → 月ごとに実在する日へクランプする。
 *  2. コメントに「timezone を考慮」と書いてありながら実装が端末ローカル時刻だった。
 *     → 店舗タイムゾーンと営業日区切り時刻を必ず受け取る。
 *  3. 会計側は営業日（深夜◯時区切り）で日付を決めているのに、
 *     期間はカレンダー日で決めていたため、締め日の深夜の会計が別の期に載っていた。
 *     → 期間の境界も営業日区切り時刻に合わせる。
 */

import { TZDate } from '@date-fns/tz';

export type ClosingDay = number | 'monthEnd';

export interface SalesPeriodRange {
  /** 期間の開始（UTC 絶対時刻。営業日区切り時刻を含む） */
  start: Date;
  /** 期間の終了（exclusive） */
  end: Date;
  /** 例: "2026-04-16 〜 2026-05-15" */
  label: string;
  /** 例: "2026-05-15"（締日で識別） */
  periodKey: string;
  /** 期間の開始営業日（YYYY-MM-DD） */
  startDate: string;
  /** 期間の最終営業日（YYYY-MM-DD, inclusive） */
  endDate: string;
}

export interface SalesPeriodOptions {
  timezone?: string;
  /** 営業日区切り時刻（0-23）。6 なら 6:00 が日付の境目 */
  boundaryHour?: number;
}

function daysInMonth(year: number, month0: number): number {
  return new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate();
}

function clampDay(year: number, month0: number, day: number): number {
  const max = daysInMonth(year, month0);
  if (day < 1) return 1;
  return Math.min(day, max);
}

function fmt(year: number, month0: number, day: number): string {
  return `${year}-${String(month0 + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** 店舗タイムゾーンの「その営業日の区切り時刻」を絶対時刻にする */
function boundaryInstant(
  year: number,
  month0: number,
  day: number,
  timezone: string,
  boundaryHour: number
): Date {
  const d = new TZDate(year, month0, day, boundaryHour, 0, 0, 0, timezone);
  return new Date(d.getTime());
}

/** 絶対時刻を、店舗タイムゾーン + 営業日区切りで見たときの年月日に落とす */
function toBusinessYmd(
  instant: Date,
  timezone: string,
  boundaryHour: number
): { year: number; month0: number; day: number } {
  const tz = new TZDate(instant, timezone);
  let year = tz.getFullYear();
  let month0 = tz.getMonth();
  let day = tz.getDate();
  if (boundaryHour > 0 && tz.getHours() < boundaryHour) {
    const prev = new TZDate(year, month0, day - 1, 12, 0, 0, 0, timezone);
    year = prev.getFullYear();
    month0 = prev.getMonth();
    day = prev.getDate();
  }
  return { year, month0, day };
}

/** 前月・翌月の (year, month0) を安全に得る */
function shiftMonth(year: number, month0: number, delta: number): { year: number; month0: number } {
  const total = year * 12 + month0 + delta;
  return { year: Math.floor(total / 12), month0: ((total % 12) + 12) % 12 };
}

/**
 * 指定日時が属する売上期間を返す。
 * referenceDate は絶対時刻。店舗タイムゾーンと営業日区切りで解釈される。
 */
export function getSalesPeriodForDate(
  referenceDate: Date,
  closingDay: ClosingDay = 'monthEnd',
  options: SalesPeriodOptions = {}
): SalesPeriodRange {
  const timezone = options.timezone ?? 'Asia/Tokyo';
  const boundaryHour = options.boundaryHour ?? 0;
  const { year, month0, day } = toBusinessYmd(referenceDate, timezone, boundaryHour);

  if (closingDay === 'monthEnd') {
    const lastDay = daysInMonth(year, month0);
    const next = shiftMonth(year, month0, 1);
    return build(
      { year, month0, day: 1 },
      { year, month0, day: lastDay },
      { year: next.year, month0: next.month0, day: 1 },
      timezone,
      boundaryHour
    );
  }

  // 締日はその月に実在する日へクランプする。
  // 例: 締日30 → 2月は 28日（閏年は29日）が締日になる。
  const closingThis = clampDay(year, month0, closingDay);

  if (day <= closingThis) {
    // 前月の締日翌日 〜 当月の締日
    const prev = shiftMonth(year, month0, -1);
    const closingPrev = clampDay(prev.year, prev.month0, closingDay);
    const startsNextMonth = closingPrev >= daysInMonth(prev.year, prev.month0);
    const startYmd = startsNextMonth
      ? { year, month0, day: 1 }
      : { year: prev.year, month0: prev.month0, day: closingPrev + 1 };
    const endExclusive = nextDay(year, month0, closingThis);
    return build(startYmd, { year, month0, day: closingThis }, endExclusive, timezone, boundaryHour);
  }

  // 当月の締日翌日 〜 翌月の締日
  const next = shiftMonth(year, month0, 1);
  const closingNext = clampDay(next.year, next.month0, closingDay);
  const startsNextMonth = closingThis >= daysInMonth(year, month0);
  const startYmd = startsNextMonth
    ? { year: next.year, month0: next.month0, day: 1 }
    : { year, month0, day: closingThis + 1 };
  const endExclusive = nextDay(next.year, next.month0, closingNext);
  return build(
    startYmd,
    { year: next.year, month0: next.month0, day: closingNext },
    endExclusive,
    timezone,
    boundaryHour
  );
}

function nextDay(year: number, month0: number, day: number): { year: number; month0: number; day: number } {
  if (day < daysInMonth(year, month0)) return { year, month0, day: day + 1 };
  const next = shiftMonth(year, month0, 1);
  return { year: next.year, month0: next.month0, day: 1 };
}

function build(
  startYmd: { year: number; month0: number; day: number },
  endInclusive: { year: number; month0: number; day: number },
  endExclusive: { year: number; month0: number; day: number },
  timezone: string,
  boundaryHour: number
): SalesPeriodRange {
  const startDate = fmt(startYmd.year, startYmd.month0, startYmd.day);
  const endDate = fmt(endInclusive.year, endInclusive.month0, endInclusive.day);
  return {
    start: boundaryInstant(startYmd.year, startYmd.month0, startYmd.day, timezone, boundaryHour),
    end: boundaryInstant(endExclusive.year, endExclusive.month0, endExclusive.day, timezone, boundaryHour),
    label: `${startDate} 〜 ${endDate}`,
    periodKey: endDate,
    startDate,
    endDate,
  };
}

/** 現在の売上期間 */
export function getCurrentSalesPeriod(
  closingDay: ClosingDay = 'monthEnd',
  options: SalesPeriodOptions = {}
): SalesPeriodRange {
  return getSalesPeriodForDate(new Date(), closingDay, options);
}

/** 指定 referenceDate の前期間 */
export function getPreviousSalesPeriod(
  referenceDate: Date,
  closingDay: ClosingDay = 'monthEnd',
  options: SalesPeriodOptions = {}
): SalesPeriodRange {
  const cur = getSalesPeriodForDate(referenceDate, closingDay, options);
  // 現期間の開始の1分前 = 前期間の最終営業日の中
  return getSalesPeriodForDate(new Date(cur.start.getTime() - 60000), closingDay, options);
}

/** 指定 referenceDate の次期間 */
export function getNextSalesPeriod(
  referenceDate: Date,
  closingDay: ClosingDay = 'monthEnd',
  options: SalesPeriodOptions = {}
): SalesPeriodRange {
  const cur = getSalesPeriodForDate(referenceDate, closingDay, options);
  return getSalesPeriodForDate(new Date(cur.end.getTime() + 60000), closingDay, options);
}
